import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface StationOwner {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  subscriptionStatus: string;
  subscriptionExpiry?: string;
  isActive?: boolean;
  isVerified?: boolean;
}

interface SubUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  permissions: any;
  isActive?: boolean;
  isVerified?: boolean;
  parentOwner: {
    id: string;
    companyName: string;
  };
}

interface AuthContextType {
  isAuthenticated: boolean;
  stationOwner: StationOwner | null;
  subUser: SubUser | null;
  userType: 'owner' | 'subuser' | null;
  login: (email: string, password: string) => Promise<StationOwner | SubUser>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [stationOwner, setStationOwner] = useState<StationOwner | null>(null);
  const [subUser, setSubUser] = useState<SubUser | null>(null);
  const [userType, setUserType] = useState<'owner' | 'subuser' | null>(null);
  const [loading, setLoading] = useState(true);

  // API base URL - Railway production URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://web-production-bdd98.up.railway.app/api';

  useEffect(() => {
    const token = localStorage.getItem('stationOwnerToken');
    const savedUserType = localStorage.getItem('userType') as 'owner' | 'subuser' | null;
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Token'ı doğrula ve kullanıcı bilgilerini al
      validateToken(savedUserType);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateToken = async (savedUserType: 'owner' | 'subuser' | null = null) => {
    try {
      // Önce ana kullanıcı profilini kontrol et
      try {
        const response = await axios.get(`${API_BASE_URL}/station-owners/profile`);
        setStationOwner({
          ...response.data,
          isActive: response.data.isActive,
          isVerified: response.data.isVerified
        });
        setSubUser(null);
        setUserType('owner');
        setIsAuthenticated(true);
        localStorage.setItem('userType', 'owner');
      } catch (ownerError) {
        // Ana kullanıcı değilse, alt kullanıcı profilini kontrol et
        try {
          const subUserResponse = await axios.get(`${API_BASE_URL}/subusers/profile`);
          setSubUser({
            ...subUserResponse.data,
            isActive: subUserResponse.data.isActive,
            isVerified: subUserResponse.data.isVerified
          });
          setStationOwner({
            id: subUserResponse.data.parentOwner.id,
            firstName: '',
            lastName: '',
            email: '',
            companyName: subUserResponse.data.parentOwner.companyName,
            subscriptionStatus: '',
            isActive: true,
            isVerified: true
          });
          setUserType('subuser');
          setIsAuthenticated(true);
          localStorage.setItem('userType', 'subuser');
        } catch (subUserError) {
          // Her iki tip de başarısız olduysa token geçersiz
          throw ownerError;
        }
      }
    } catch (error) {
      localStorage.removeItem('stationOwnerToken');
      localStorage.removeItem('userType');
      delete axios.defaults.headers.common['Authorization'];
      setIsAuthenticated(false);
      setStationOwner(null);
      setSubUser(null);
      setUserType(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      // Önce ana kullanıcı girişini dene
      try {
        const response = await axios.post(`${API_BASE_URL}/station-owners/login`, {
          email,
          password
        });

        const { token, stationOwner: ownerData } = response.data;
        
        localStorage.setItem('stationOwnerToken', token);
        localStorage.setItem('userType', 'owner');
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        setStationOwner({
          ...ownerData,
          isActive: ownerData.isActive,
          isVerified: ownerData.isVerified
        });
        setSubUser(null);
        setUserType('owner');
        setIsAuthenticated(true);
        
        return ownerData;
      } catch (ownerError: any) {
        // Ana kullanıcı değilse, alt kullanıcı girişini dene
        // 400 veya 401 hatası alındıysa (email/password yanlış veya kullanıcı bulunamadı)
        if (ownerError.response?.status === 400 || ownerError.response?.status === 401) {
          try {
            const subUserResponse = await axios.post(`${API_BASE_URL}/subusers/login`, {
              email,
              password
            });

            const { token, subUser: subUserData } = subUserResponse.data;
            
            localStorage.setItem('stationOwnerToken', token);
            localStorage.setItem('userType', 'subuser');
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            
            setSubUser({
              ...subUserData,
              isActive: subUserData.isActive || true,
              isVerified: subUserData.isVerified || false
            });
            setStationOwner({
              id: subUserData.parentOwner.id,
              firstName: '',
              lastName: '',
              email: '',
              companyName: subUserData.parentOwner.companyName,
              subscriptionStatus: '',
              isActive: true,
              isVerified: true
            });
            setUserType('subuser');
            setIsAuthenticated(true);
            
            return subUserData;
          } catch (subUserError: any) {
            // Alt kullanıcı girişi de başarısız olduysa, daha açıklayıcı hata mesajı göster
            const errorMessage = subUserError.response?.data?.message || ownerError.response?.data?.message || 'Giriş yapılamadı. E-posta ve şifrenizi kontrol edin.';
            throw new Error(errorMessage);
          }
        } else {
          // Diğer hatalar için ana kullanıcı hatasını göster
          throw new Error(ownerError.response?.data?.message || 'Giriş yapılamadı');
        }
      }
    } catch (error: any) {
      // Error zaten Error objesi ise direkt throw et, değilse yeni Error oluştur
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(error.response?.data?.message || error.message || 'Giriş yapılamadı');
    }
  };

  const logout = () => {
    localStorage.removeItem('stationOwnerToken');
    localStorage.removeItem('userType');
    delete axios.defaults.headers.common['Authorization'];
    setStationOwner(null);
    setSubUser(null);
    setUserType(null);
    setIsAuthenticated(false);
  };

  const value: AuthContextType = {
    isAuthenticated,
    stationOwner,
    subUser,
    userType,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
